import { useId, useState } from "react";

import { BILLING_CONTACT_COPY } from "./transactions.constants.ts";
import type { BillingContactView } from "./transactions.model.ts";

/**
 * Who at this firm receives the ONDO fee notices, and the monthly statement and
 * invoice — OND-93.
 *
 * ON THE TRANSACTION CENTRE, beside the ledger it describes. The argument is in
 * `BILLING_CONTACT_COPY`'s header: a fee notice is not a notification, so this
 * does not belong on the notification settings screen.
 *
 * IT NAMES THE PERSON TO EVERYBODY AND OFFERS THE CONTROL TO A DIRECTOR, which
 * is the same split the fee rows above it make. Who the firm's post goes to is a
 * fact about the firm that its own people may read; choosing it commits the
 * firm.
 *
 * NO ADDRESS IS SHOWN, and that is deliberate rather than an omission. The
 * response carries none — an address is read inside the activity that sends and
 * nowhere else — because this panel is readable by every seat, and a colleague's
 * personal address on it is the disclosure `20261003093000` decision 6 took
 * three columns out of a table for.
 *
 * THE CHOICE IS A SELECT OVER THE ROSTER, not a typed ONDO id. The roster is
 * already readable by any seat, so offering it discloses nothing new — and the
 * alternative is a director typing an id, which is how the wrong colleague gets
 * the firm's invoices. "The default administrator" is the empty option and
 * clears the choice, because the default is always underneath.
 */
export function BillingContactPanel({
  billing,
  canChoose,
  changing,
  onChoose,
}: {
  billing: BillingContactView;
  canChoose: boolean;
  changing: boolean;
  onChoose: (contactOndoId: string | null) => void;
}) {
  const selectId = useId();
  const [picked, setPicked] = useState<string | null>(null);

  if (billing.kind === "loading") {
    return (
      <section className="transactions__billing">
        <h2 className="transactions__billing-title">{BILLING_CONTACT_COPY.title}</h2>
        <p className="gallery__note" role="status">
          {billing.message}
        </p>
      </section>
    );
  }

  if (billing.kind === "nobody") {
    return (
      <section className="transactions__billing">
        <h2 className="transactions__billing-title">{BILLING_CONTACT_COPY.title}</h2>
        <p className="gallery__note">{billing.message}</p>
      </section>
    );
  }

  // The select's value is the pending pick until a change lands, then the
  // server's answer again — so clearing the choice shows whoever the default
  // turned out to be rather than an empty control.
  const value = picked ?? billing.contactOndoId;

  return (
    <section className="transactions__billing">
      <h2 className="transactions__billing-title">{BILLING_CONTACT_COPY.title}</h2>
      <p className="transactions__billing-lead">{BILLING_CONTACT_COPY.lead}</p>

      <p className="transactions__billing-contact">
        <strong>{billing.contact}</strong>
      </p>
      <p className="gallery__note gallery__note--quiet">{billing.standing}</p>

      {canChoose && billing.candidates.length > 0 ? (
        <form
          className="transactions__billing-form"
          onSubmit={(event) => {
            event.preventDefault();
            onChoose(picked === "" ? null : (picked ?? billing.contactOndoId));
          }}
        >
          <label className="transactions__billing-label" htmlFor={selectId}>
            {BILLING_CONTACT_COPY.chooseLabel}
          </label>
          <select
            id={selectId}
            className="transactions__billing-select"
            value={value}
            disabled={changing}
            onChange={(event) => setPicked(event.target.value)}
          >
            <option value="">{BILLING_CONTACT_COPY.keepDefault}</option>
            {billing.candidates.map((candidate) => (
              <option key={candidate.ondoId} value={candidate.ondoId}>
                {candidate.label}
              </option>
            ))}
          </select>
          <button type="submit" className="transactions__billing-save" disabled={changing}>
            {changing ? BILLING_CONTACT_COPY.saving : BILLING_CONTACT_COPY.save}
          </button>
        </form>
      ) : (
        <p className="gallery__note gallery__note--quiet">{BILLING_CONTACT_COPY.needsAdmin}</p>
      )}

      <p className="gallery__note gallery__note--quiet">{BILLING_CONTACT_COPY.note}</p>
    </section>
  );
}
